import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, shortDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import { CheckCircle, Warning, XCircle } from "@phosphor-icons/react";

export default function LoanClosurePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming"); // "upcoming" | "matured"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closeLoan, setCloseLoan] = useState(null);
  const [form, setForm] = useState({ closure_type: "closed", penalty_type: "percent", penalty_value: "", note: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/loans/closure/list?days=365&include_matured=true");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const canClose = ["super_admin", "branch_admin", "staff"].includes(user.role);

  const upcoming = rows.filter((r) => !r.is_matured);
  const matured = rows.filter((r) => r.is_matured);
  const list = tab === "upcoming" ? upcoming : matured;

  const openClose = (loan, defaultType = "preclosed") => {
    setCloseLoan(loan);
    setForm({
      closure_type: defaultType,
      penalty_type: "percent",
      penalty_value: defaultType === "preclosed" ? 2 : "",
      note: "",
    });
    setErr("");
  };

  const previewCharge = () => {
    if (!closeLoan || form.closure_type !== "preclosed") return 0;
    const v = Number(form.penalty_value) || 0;
    if (form.penalty_type === "flat") return v;
    return Math.round(((closeLoan.outstanding || 0) * v / 100) * 100) / 100;
  };
  const previewSettlement = () => {
    if (!closeLoan) return 0;
    return Math.round(((closeLoan.outstanding || 0) + previewCharge()) * 100) / 100;
  };

  const submitClose = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const body = { closure_type: form.closure_type };
      if (form.closure_type === "preclosed" && form.penalty_value !== "" && form.penalty_value != null) {
        body.penalty_type = form.penalty_type;
        body.penalty_value = Number(form.penalty_value);
      }
      if (form.note) body.note = form.note;
      body.settlement_amount = previewSettlement();
      await api.post(`/loans/${closeLoan.id}/close`, body);
      setCloseLoan(null);
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Failed to close loan");
    } finally { setSaving(false); }
  };

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up" data-testid="loan-closure-page">
      <PageHeader
        title="Loan Closure"
        subtitle="Loans nearing final EMI, and matured loans pending closure."
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          {[
            ["upcoming", `Upcoming (${upcoming.length})`, CheckCircle],
            ["matured", `Matured (${matured.length})`, Warning],
          ].map(([v, l, Icon]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              data-testid={`closure-tab-${v}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                tab === v
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              <Icon size={15} weight={tab === v ? "fill" : "regular"} />
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : list.length === 0 ? (
          <EmptyState title={tab === "upcoming" ? "No loans nearing closure" : "No matured loans pending"} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-right px-4 py-3">Principal</th>
                  <th className="text-right px-4 py-3">Outstanding</th>
                  <th className="text-left px-4 py-3">Last EMI due</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((l) => {
                  const days = l.last_due_date
                    ? Math.ceil((new Date(l.last_due_date) - new Date()) / (1000 * 3600 * 24))
                    : null;
                  return (
                    <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`closure-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{l.customer_name || "—"}</div>
                        <div className="text-xs text-slate-500">{l.customer_phone || ""}</div>
                      </td>
                      <td className="px-4 py-3"><Badge tone="info">{l.loan_type}</Badge></td>
                      <td className="px-4 py-3 mono text-right">{money(l.principal)}</td>
                      <td className="px-4 py-3 mono text-right font-medium">{money(l.outstanding)}</td>
                      <td className="px-4 py-3 mono text-slate-700">{shortDate(l.last_due_date)}</td>
                      <td className="px-4 py-3">
                        {l.is_matured ? (
                          <Badge tone="danger">Matured</Badge>
                        ) : (
                          <Badge tone="warning">
                            <span className="text-amber-700"><span className="blink-dot" />{days}d left</span>
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link to={`/loans/${l.id}`} className="text-emerald-700 hover:underline text-sm mr-3" data-testid={`open-loan-${l.id}`}>Open</Link>
                        {canClose && (
                          <button
                            onClick={() => openClose(l, l.is_matured ? "closed" : "preclosed")}
                            data-testid={`close-loan-${l.id}`}
                            className="inline-flex items-center gap-1 text-rose-700 text-sm font-medium hover:underline"
                          >
                            <XCircle size={14} weight="bold" /> {l.is_matured ? "Close" : "Pre-close"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={!!closeLoan} onClose={() => setCloseLoan(null)} title="Close loan" maxWidth="max-w-lg">
        {closeLoan && (
          <form onSubmit={submitClose} className="space-y-4">
            <div className="text-sm text-slate-600">
              <div><span className="text-slate-400">Loan:</span> <span className="mono">{closeLoan.loan_type} #{closeLoan.id.slice(0, 8)}</span></div>
              <div><span className="text-slate-400">Customer:</span> {closeLoan.customer_name}</div>
              <div><span className="text-slate-400">Outstanding:</span> <span className="mono font-semibold text-rose-700">{money(closeLoan.outstanding || 0)}</span></div>
              <div><span className="text-slate-400">Last EMI:</span> <span className="mono">{shortDate(closeLoan.last_due_date)}</span></div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Closure type</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["closed", "Regular closure", "No extra charge"],
                  ["preclosed", "Pre-closure", "Extra charge applies"],
                ].map(([v, l, s]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setForm({ ...form, closure_type: v })}
                    data-testid={`loan-closure-type-${v}`}
                    className={`text-left border rounded-lg px-3 py-2 ${
                      form.closure_type === v ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <div className="text-sm font-semibold">{l}</div>
                    <div className="text-xs text-slate-500">{s}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.closure_type === "preclosed" && (
              <div className="grid grid-cols-2 gap-3">
                <Select label="Charge type" testId="preclose-charge-type"
                  value={form.penalty_type} onChange={(e) => setForm({ ...form, penalty_type: e.target.value })}>
                  <option value="percent">Percent of outstanding</option>
                  <option value="flat">Flat rupees</option>
                </Select>
                <Input
                  label={form.penalty_type === "percent" ? "Charge %" : "Charge (₹)"}
                  type="number" step="0.01" testId="preclose-charge-value"
                  value={form.penalty_value} onChange={(e) => setForm({ ...form, penalty_value: e.target.value })}
                />
              </div>
            )}
            <Input label="Note (optional)" testId="loan-closure-note"
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Outstanding</span><span className="mono">{money(closeLoan.outstanding || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Pre-closure charge</span><span className="mono text-rose-600">+ {money(previewCharge())}</span></div>
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1">
                <span>Settlement amount</span>
                <span className="mono text-emerald-700" data-testid="loan-settlement-preview">{money(previewSettlement())}</span>
              </div>
            </div>

            {err && <div className="text-sm text-rose-600">{String(err)}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" type="button" onClick={() => setCloseLoan(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="confirm-close-loan">
                {saving ? "Closing…" : "Confirm closure"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
